import React from 'react';
import {
  C,
  CARD_W,
  COUNTDOWN_TIMER_H,
  nx,
  ny,
} from '../flowCanvas.constants';

const VISUAL_OFFSET_Y = -96;

// ─── Layout constants ──────────────────────────────────────────────────────────
const HEADER_H  = 48;
const DIAL_SIZE = 64;
const PILL_H    = 22;
const PILL_GAP  = 5;
const LABEL_X   = 12;

// Countdown-specific presets (in milliseconds)
const PRESETS = [
  { label: '10s',  ms: 10000  },
  { label: '30s',  ms: 30000  },
  { label: '1m',   ms: 60000  },
  { label: '3m',   ms: 180000 },
  { label: '5m',   ms: 300000 },
  { label: '10m',  ms: 600000 },
];

const DISPLAY_OPTIONS = [
  { value: 'hms', label: 'H:M:S'  },
  { value: 'ms',  label: 'M:S'    },
  { value: 'hm',  label: 'H:M'    },
  { value: 's',   label: 'Secs'   },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatMs(ms) {
  const totalSec = Math.round(Number(ms) / 1000);
  if (totalSec <= 0) return '—';
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function toBool(val) {
  if (val === true || val === false) return val;
  if (typeof val === 'string') {
    const l = val.trim().toLowerCase();
    if (l === 'true') return true;
    if (l === 'false') return false;
  }
  if (typeof val === 'number') return val === 1;
  return false;
}

function getSettings(step, liveCustomisationData) {
  const cfg     = step?.customisationConfig;
  const tabKey  = cfg?.tabKey;
  const varKey  = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const live    = tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
    : null;
  const vals = Array.isArray(live)
    ? live
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return vals?.[0] || {};
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function TogglePill({ x, y, width, active, label, onClick }) {
  return (
    <g onClick={(e) => { e.stopPropagation(); onClick?.(); }} style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={y} width={width} height={PILL_H} rx={PILL_H / 2}
        fill={active ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)'}
        stroke={active ? 'rgba(74,222,128,0.58)' : 'rgba(145,70,255,0.22)'}
        strokeWidth="1"
      />
      <circle cx={x + (active ? width - 11 : 11)} cy={y + PILL_H / 2} r="7"
        fill={active ? C.green : C.textMuted} />
      <text
        x={x + width / 2} y={y + PILL_H / 2 + 3.5}
        textAnchor="middle" fontSize="9" fontWeight="700"
        fill={active ? C.green : C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

function PresetPill({ x, y, w, label, active, onClick }) {
  return (
    <g onClick={(e) => { e.stopPropagation(); onClick?.(); }} style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={y} width={w} height={PILL_H} rx={PILL_H / 2}
        fill={active ? 'rgba(145,70,255,0.22)' : 'rgba(255,255,255,0.04)'}
        stroke={active ? C.purple : 'rgba(145,70,255,0.20)'}
        strokeWidth={active ? 1.5 : 1}
      />
      <text
        x={x + w / 2} y={y + PILL_H / 2 + 3.5}
        textAnchor="middle" fontSize="9" fontWeight={active ? '800' : '600'}
        fill={active ? C.purple : C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

function SelectControl({ x, y, width, value, options, onChange }) {
  return (
    <foreignObject x={x} y={y} width={width} height="24" style={{ pointerEvents: 'auto' }}>
      <select
        value={value ?? ''}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => { e.stopPropagation(); onChange?.(e.target.value); }}
        style={{
          width: '100%',
          height: '22px',
          borderRadius: '11px',
          border: '1px solid rgba(145,70,255,0.22)',
          background: 'rgba(255,255,255,0.05)',
          color: '#ffffff',
          fontSize: '9px',
          fontWeight: 700,
          padding: '0 8px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </foreignObject>
  );
}

// ─── Main Node ─────────────────────────────────────────────────────────────────
export default function CountdownTimerNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
}) {
  const x    = overrideX ?? nx(step);
  const y    = (overrideY ?? ny(step, centreY)) + VISUAL_OFFSET_Y;
  const left = x - CARD_W / 2;
  const top  = y - COUNTDOWN_TIMER_H / 2;

  const settings    = getSettings(step, liveCustomisationData);
  const timeSecRaw  = settings?.time;
  const timeSec     = Number.isFinite(Number(timeSecRaw)) && Number(timeSecRaw) > 0 ? Number(timeSecRaw) : 60;
  const timeMs      = timeSec * 1000;
  const displayType = settings?.displayType ?? 'hms';
  const showHunds   = toBool(settings?.showHunds ?? false);
  const timeLabel   = formatMs(timeMs);

  // Clock-hand: position based on seconds within the current minute
  const totalSec   = Math.round(timeMs / 1000);
  const secInMin   = ((totalSec % 60) + 60) % 60;

  // Layout
  const pillRowY  = top + HEADER_H + DIAL_SIZE + 52;
  const innerW    = CARD_W - LABEL_X * 2;
  const pillW     = Math.floor((innerW - PILL_GAP * (PRESETS.length - 1)) / PRESETS.length);
  const displayRowY = pillRowY + PILL_H + 16;
  const toggleRowY  = displayRowY + PILL_H + 14;

  // Display-type select
  const dtLabelW  = 72;
  const dtCtrlW   = innerW - dtLabelW - 8;

  // Toggle
  const togLabelW = 72;
  const togCtrlW  = innerW - togLabelW - 8;

  // Clock dial
  const dialCX = left + CARD_W / 2;
  const dialCY = top + HEADER_H + DIAL_SIZE / 2 + 16;
  const dialR  = DIAL_SIZE / 2;

  return (
    <g
      className="fc-node fc-node--countdown-timer"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Drop shadow */}
      <rect
        x={left + 2} y={top + 4}
        width={CARD_W} height={COUNTDOWN_TIMER_H}
        rx="10" fill="#000" fillOpacity="0.3"
      />

      {/* Card background */}
      <rect
        x={left} y={top}
        width={CARD_W} height={COUNTDOWN_TIMER_H}
        rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* Header divider */}
      <line
        x1={left + 1} y1={top + HEADER_H}
        x2={left + CARD_W - 1} y2={top + HEADER_H}
        stroke={C.border} strokeWidth="1"
      />

      {/* Title */}
      <text
        x={left + 14} y={top + 25}
        fontSize="11.5" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {step.stepTitle || 'HMS Timer'}
      </text>
      <text
        x={left + 14} y={top + 39}
        fontSize="8.5" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Countdown duration
      </text>

      {/* ── Clock dial ──────────────────────────────────────────────────────── */}
      <circle cx={dialCX} cy={dialCY} r={dialR}
        fill="rgba(145,70,255,0.06)"
        stroke={C.purple} strokeWidth="1.5"
      />
      {Array.from({ length: 12 }, (_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const inner = dialR - 6;
        const outer = dialR - 2;
        return (
          <line
            key={i}
            x1={dialCX + inner * Math.cos(angle)}
            y1={dialCY + inner * Math.sin(angle)}
            x2={dialCX + outer * Math.cos(angle)}
            y2={dialCY + outer * Math.sin(angle)}
            stroke={i % 3 === 0 ? C.purple : 'rgba(145,70,255,0.30)'}
            strokeWidth={i % 3 === 0 ? 1.5 : 1}
          />
        );
      })}
      {/* Clock hand */}
      {(() => {
        const angle = ((secInMin / 60) * 360 - 90) * (Math.PI / 180);
        const handR = dialR - 9;
        return (
          <line
            x1={dialCX} y1={dialCY}
            x2={dialCX + handR * Math.cos(angle)}
            y2={dialCY + handR * Math.sin(angle)}
            stroke={C.purple} strokeWidth="2" strokeLinecap="round"
          />
        );
      })()}
      <circle cx={dialCX} cy={dialCY} r="3" fill={C.purple} />

      {/* Time label under dial */}
      <text
        x={dialCX} y={dialCY + dialR + 21}
        textAnchor="middle" fontSize="15" fontWeight="800"
        fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {timeLabel}
      </text>

      {/* ── Preset pills ─────────────────────────────────────────────────────── */}
      {PRESETS.map((preset, i) => (
        <PresetPill
          key={preset.label}
          x={left + LABEL_X + i * (pillW + PILL_GAP)}
          y={pillRowY}
          w={pillW}
          label={preset.label}
          active={timeMs === preset.ms}
          onClick={() => onFieldChange?.('time', preset.ms / 1000)}
        />
      ))}

      {/* ── Display type ─────────────────────────────────────────────────────── */}
      <line
        x1={left + 10} y1={displayRowY - 6}
        x2={left + CARD_W - 10} y2={displayRowY - 6}
        stroke={C.border} strokeWidth="1"
      />
      <text
        x={left + LABEL_X} y={displayRowY + PILL_H / 2 + 3.5}
        fontSize="9" fontWeight="700" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Display
      </text>
      <SelectControl
        x={left + LABEL_X + dtLabelW + 8}
        y={displayRowY}
        width={dtCtrlW}
        value={displayType}
        options={DISPLAY_OPTIONS}
        onChange={(val) => onFieldChange?.('displayType', val)}
      />

      {/* ── Show hundredths toggle ───────────────────────────────────────────── */}
      <text
        x={left + LABEL_X} y={toggleRowY + PILL_H / 2 + 3.5}
        fontSize="9" fontWeight="700" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        Hundredths
      </text>
      <TogglePill
        x={left + LABEL_X + togLabelW + 8}
        y={toggleRowY}
        width={togCtrlW}
        active={showHunds}
        label={showHunds ? 'Shown' : 'Hidden'}
        onClick={() => onFieldChange?.('showHunds', !showHunds)}
      />
    </g>
  );
}
